import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import {
  isPromoStoreDescriptionImageUrl,
  sanitizePromoStoreDescriptionHtml,
} from "./sanitizeHtml";

const isSafeLinkUrl = (value) =>
  /^(?:https?:\/\/|mailto:)[^\s<>"]+$/i.test(String(value || "").trim());

/**
 * PromoStore-only description editor. Project, Sales Order, Estimate and
 * Invoicing Cart editing continue to use the existing TextArea.
 * Values remain HTML in the existing Description field.
 */
const PromoStoreDescriptionEditor = ({ value = "", onChange }) => {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const [mode, setMode] = useState("visual");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (mode !== "visual" || !editorRef.current) return;
    const nextHtml = sanitizePromoStoreDescriptionHtml(value);
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [mode, value]);

  const publish = (html) => {
    const safeHtml = sanitizePromoStoreDescriptionHtml(html);
    if (editorRef.current && editorRef.current.innerHTML !== safeHtml) {
      editorRef.current.innerHTML = safeHtml;
    }
    onChange?.(safeHtml);
  };

  const rememberSelection = () => {
    const selection = window.getSelection?.();
    if (selection?.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    editorRef.current?.focus();
    const selection = window.getSelection?.();
    if (selection && selectionRef.current) {
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
  };

  const command = (action, argument = null) => {
    restoreSelection();
    document.execCommand?.(action, false, argument);
    rememberSelection();
    if (editorRef.current) publish(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    rememberSelection();
    const url = window.prompt("Link URL (https:// or mailto:)")?.trim();
    if (!url) return;
    if (!isSafeLinkUrl(url)) {
      setMessage("Enter a valid HTTPS, HTTP or email link.");
      return;
    }
    setMessage("");
    restoreSelection();
    const selection = window.getSelection?.();
    if (selection && !selection.isCollapsed) {
      command("createLink", url);
    } else {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.textContent = url;
      command("insertHTML", anchor.outerHTML);
    }
  };

  const insertImage = () => {
    rememberSelection();
    const url = window.prompt("Image URL (https://)")?.trim();
    if (!url) return;
    if (!isPromoStoreDescriptionImageUrl(url)) {
      setMessage("Enter a valid HTTP or HTTPS image URL.");
      return;
    }
    setMessage("");
    command("insertImage", url);
  };

  const onPaste = (event) => {
    event.preventDefault();
    const clipboard = event.clipboardData;
    const html = clipboard?.getData("text/html");
    const plain = clipboard?.getData("text/plain");
    if (!html && !plain) return;
    if (html) {
      command("insertHTML", sanitizePromoStoreDescriptionHtml(html));
    } else {
      command("insertText", plain);
    }
  };

  const changeMode = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === "visual") {
      onChange?.(sanitizePromoStoreDescriptionHtml(value));
    } else if (editorRef.current) {
      publish(editorRef.current.innerHTML);
    }
    setMode(nextMode);
    setMessage("");
  };

  const toolbarButton = (name, text, action, arg = null) => (
    <Button
      key={name}
      type="button"
      size="small"
      variant="outlined"
      aria-label={name}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => command(action, arg)}
      sx={{ minWidth: 0, textTransform: "none" }}
    >
      {text}
    </Button>
  );

  return (
    <Box>
      <Typography sx={{ fontSize: "0.9rem", color: "#555", mb: 0.5 }}>
        Product Description
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button type="button" size="small" variant={mode === "visual" ? "contained" : "outlined"}
          onClick={() => changeMode("visual")} aria-label="Visual editor">Visual</Button>
        <Button type="button" size="small" variant={mode === "html" ? "contained" : "outlined"}
          onClick={() => changeMode("html")} aria-label="HTML source editor">HTML</Button>
      </Stack>
      {mode === "visual" ? (
        <>
          <Stack direction="row" gap={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
            {toolbarButton("Bold", "B", "bold")}
            {toolbarButton("Italic", "I", "italic")}
            {toolbarButton("Underline", "U", "underline")}
            {toolbarButton("Heading", "Heading", "formatBlock", "h3")}
            {toolbarButton("Bullet list", "Bullets", "insertUnorderedList")}
            {toolbarButton("Numbered list", "Numbers", "insertOrderedList")}
            {toolbarButton("Block quote", "Quote", "formatBlock", "blockquote")}
            <Button type="button" size="small" variant="outlined" onMouseDown={(event) => event.preventDefault()}
              onClick={insertLink} sx={{ textTransform: "none" }}>Link</Button>
            <Button type="button" size="small" variant="outlined" onMouseDown={(event) => event.preventDefault()}
              onClick={insertImage} sx={{ textTransform: "none" }}>Insert image</Button>
          </Stack>
          <Box
            ref={editorRef}
            role="textbox"
            aria-label="Product Description"
            aria-multiline="true"
            contentEditable
            suppressContentEditableWarning
            onKeyUp={rememberSelection}
            onMouseUp={rememberSelection}
            onInput={(event) => publish(event.currentTarget.innerHTML)}
            onBlur={(event) => publish(event.currentTarget.innerHTML)}
            onPaste={onPaste}
            onDrop={(event) => event.preventDefault()}
            sx={{
              minHeight: 156, p: 1.5, border: "1px solid #cbd5e1",
              borderRadius: 1, backgroundColor: "#fff", outlineColor: "primary.main",
              overflowWrap: "anywhere",
              "& img": { maxWidth: "100%", height: "auto" },
              "& p": { my: 1 },
              "& a": { overflowWrap: "anywhere" },
            }}
          />
        </>
      ) : (
        <TextField
          label="HTML source"
          aria-label="HTML source"
          multiline
          fullWidth
          minRows={8}
          value={String(value ?? "")}
          onChange={(event) => onChange?.(event.target.value)}
          onBlur={(event) => onChange?.(sanitizePromoStoreDescriptionHtml(event.target.value))}
          helperText="Allowed: headings, bold, italic, underline, lists, quotes, links and images. Scripts, embeds and unsafe HTML are removed."
        />
      )}
      {message ? <Typography role="alert" color="error" variant="caption">{message}</Typography> : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
        Add an image with a public HTTPS image URL, or paste supported HTML. Switch to HTML to edit the markup.
      </Typography>
    </Box>
  );
};

export default PromoStoreDescriptionEditor;
